// photos — photographs taken with no signal, kept as blobs on the phone and
// uploaded on reconnect, AFTER the ops queue has replayed (a photo of work
// belongs after the clock-in that started the work).
//
// Same three outcomes as queue.js: uploaded (removed), refused by the server
// (moved to failed with the sentence), or no answer (kept). Same storage
// adapter shape; the fallback when IndexedDB is missing is memory, because a
// Blob does not survive JSON and a photo silently turned into "{}" is worse
// than a photo that is lost with the tab.

import { request, isUnreachable } from '../../shared/http';
import { resilientStore, uuid } from './storage';
import { mayReplay, needsAdoption } from './owner';

export function uploadPath(jobId) { return `/media/job/${jobId}`; }

/**
 * One photo → the multipart POST the existing media route accepts.
 *
 * ONE PHOTO, and do not batch this. server/routes/media.js takes up to
 * MAX_FILES_PER_UPLOAD files per request and apps/shared/photoBatches.js
 * chunks up to that on the office path — but the queue must not, and the
 * reason is the `client_key` two lines down. server/utils/clientOps.js keys
 * the whole request, so twelve photos behind one key means a lost answer
 * re-sends all twelve, and one refusal fails all twelve. This queue keeps a
 * photo until the server ANSWERS, which makes that exactly the case it exists
 * to survive.
 *
 * Checked 2026-09-13 while tracing a crew member's "can't upload more than 10
 * at a time": this path never met that cap, because it never puts two photos
 * in a request.
 */
export function buildUpload(photo) {
  const fd = new FormData();
  const name = photo.name || `photo-${photo.key}.jpg`;
  fd.append('files', photo.blob, name);
  fd.append('captions', JSON.stringify([photo.caption || '']));
  fd.append('client_key', photo.key);
  return fd;
}

export function defaultUpload(photo) {
  return request(uploadPath(photo.job_id), { method: 'POST', body: buildUpload(photo) });
}

export function createPhotoQueue({ store, now = () => new Date(), newKey = uuid, upload, owner = null } = {}) {
  const backing = store || resilientStore('photos', { jsonSafe: false });
  const uploadFn = upload || defaultUpload;
  let items = [];
  let currentOwner = owner;
  const listeners = new Set();
  let lock = Promise.resolve();

  const notify = (event) => { listeners.forEach((fn) => { try { fn(event); } catch (_) { /* */ } }); };
  const sortByCreated = (rows) => rows.slice().sort((a, b) => (a.created < b.created ? -1 : a.created > b.created ? 1 : 0));

  const ready = backing.all()
    .then((rows) => { items = sortByCreated(rows.filter((r) => r && r.key && r.blob)); notify('loaded'); })
    .catch(() => { items = []; notify('loaded'); });
  const persist = (record) => backing.put(record).catch(() => {});
  const erase = (key) => backing.remove(key).catch(() => {});

  const pq = {
    ready,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },

    /** Who this queue is currently uploading for — see ./owner.js. */
    setOwner(id) {
      const next = id === undefined || id === null || id === '' ? null : id;
      if (String(next) === String(currentOwner)) return;
      currentOwner = next;
      notify('changed');
    },
    owner() { return currentOwner; },

    adoptOrphans() {
      if (currentOwner === null) return 0;
      const orphans = items.filter(needsAdoption);
      if (!orphans.length) return 0;
      const adopted = orphans.map((r) => ({ ...r, owner: currentOwner }));
      const byKey = new Map(adopted.map((r) => [r.key, r]));
      items = items.map((r) => byKey.get(r.key) || r);
      adopted.forEach(persist);
      notify('changed');
      return adopted.length;
    },

    enqueue({ key, job_id, blob, caption = '', name = '' }) {
      const record = { key: key || newKey(), job_id, blob, caption, name, created: now().toISOString(), status: 'pending', owner: currentOwner };
      items = sortByCreated([...items.filter((r) => r.key !== record.key), record]);
      persist(record);
      notify('changed');
      return record;
    },

    pending() { return items.filter((r) => r.status !== 'failed'); },
    pendingFor(jobId) { return pq.pending().filter((r) => String(r.job_id) === String(jobId)); },
    failed() { return items.filter((r) => r.status === 'failed'); },

    remove(key) {
      items = items.filter((r) => r.key !== key);
      erase(key);
      notify('changed');
    },
    /**
     * Same rule, and the same reason, as the op queue's dismissFailed: only a
     * FAILED record is dismissible. useOffline routes a dismiss to whichever
     * queue holds the key, so a tap aimed at a failed upload could land on a
     * PENDING one and erase a photo the crew member watched the app accept.
     * A job photo is not payroll hours, but it is often the only record that
     * a property looked the way the crew says it did.
     */
    dismissFailed(key) {
      const before = items.length;
      if (key === undefined) {
        for (const r of items.filter((x) => x.status === 'failed')) erase(r.key);
        items = items.filter((r) => r.status !== 'failed');
      } else {
        const target = items.find((r) => r.key === key && r.status === 'failed');
        if (!target) return;
        items = items.filter((r) => r !== target);
        erase(key);
      }
      if (items.length !== before) notify('changed');
    },

    /** Upload pending photos one at a time, in order. Never rejects. */
    replay(uploadOverride) {
      const doUpload = uploadOverride || uploadFn;
      const run = async () => {
        await ready;
        const out = { sent: 0, failed: 0, kept: 0, offline: false, error: null };
        // This person's photos only. A colleague's queued photo would upload
        // under THIS bearer token and attach to the job as their work — the
        // same misattribution as the ops queue, in job_media instead of
        // time_entries (./owner.js).
        const mine = pq.pending().filter((r) => mayReplay(r, currentOwner));
        for (const record of mine) {
          try {
            await doUpload(record);
            items = items.filter((x) => x.key !== record.key);
            erase(record.key);
            out.sent++;
          } catch (e) {
            // What is still waiting, of THIS person's: the ones already
            // uploaded or refused are no longer kept.
            if (isUnreachable(e)) { out.offline = true; out.kept = mine.length - out.sent - out.failed; break; }
            const failedRec = { ...record, status: 'failed', error: (e && e.message) || 'Could not be sent' };
            items = items.map((x) => (x.key === record.key ? failedRec : x));
            persist(failedRec);
            out.failed++;
          }
        }
        notify('changed');
        if (out.sent > 0 || out.failed > 0) notify('replayed');
        return out;
      };
      const p = lock.then(run, run);
      lock = p.catch(() => {});
      return p;
    },
  };
  return pq;
}

let shared = null;
export function getPhotoQueue() {
  if (!shared) shared = createPhotoQueue();
  return shared;
}
export function setPhotoQueue(pq) { shared = pq; }
